import {
    filter,
    groupBy,
    isEmpty,
    map,
    sortBy,
    sumBy,
    toString,
} from 'lodash';

export default {

    /**
     * Functional getter for user
     *
     * @param {object} state Vuex state.
     *
     * @returns {object} User data
     */
    getUser: (state) => (key) => {
        return (state.user || {})[key] || null;
    },

    /**
     * Retursn avatar URL
     *
     * @param {object} state Vuex state.
     *
     * @returns {string} Avatar url
     */
    avatarUrl(state) {
        return state.user?.meta_data?.avatar || false;
    },

    /**
     * Prepares sorted home widgets.
     *
     * @param {object} state State object.
     * @returns {object} Sorted home widgets.
     */
    sortedHomeWidgets(state) {
        const allowedTypes = [
            'all',
            ...(state.user ? (state.user.pro ? ['pro'] : ['free']) : []),
        ];

        return map(
            groupBy(
                filter(
                    state.homeWidgets,
                    (item) => allowedTypes.includes(item.subscriptionType)
                ),
                'column'
            ),
            (column) => sortBy(column, 'order')
        );
    },

    /**
     * Functional getter for user meta.
     *
     * Usage:
     *      store.getters.getUserMeta('meta_key');
     *
     * @param   {object} state State.
     * @returns {Function}     Getter function.
     */
    getUserMeta: (state) => (key) => {
        return (state.user?.meta_data || {})[key] || null;
    },

    /**
     * Returns user card
     *
     * @param {object} state Vuex state.
     *
     * @returns {object} User card data.
     */
    userCard(state) {
        if (! isEmpty(state.userCard)) {
            const card = state.userCard;

            let month = card.exp_month;
            const year = card.exp_year;
            const brand = card.brand;

            if (2 > month.toString().length) {
                month = '0' + month;
            }

            card.expiryDate = `${month}/${year}`;

            if ('visa' === brand || 'mastercard' === brand) {
                card.brandClass = `fab fa-cc-${brand}`;
            } else {
                card.brandClass = 'fal fa-credit-card';
            }

            return card;
        }

        return state.userCard;
    },

    /**
     * Returns user subscription details
     *
     * @param   {object}  state  Vuex state.
     *
     * @returns  {object}         Subscription details.
     */
    userSubscriptionDetails(state) {
        if (! isEmpty(state.userSubscription)) {
            const { name } = state.userSubscription;
            const subscriptionDetails = state.userSubscription.items.data[0];
            const interval = subscriptionDetails.plan.interval;
            const amount = subscriptionDetails.plan.amount * subscriptionDetails.quantity;
            let nickname = subscriptionDetails.plan.nickname;

            if (isEmpty(nickname)) {
                switch (interval) {
                case 'month':
                    nickname = 'Monthly';
                    break;
                case 'year':
                    nickname = 'Yearly';
                    break;
                }
            }

            return {
                subscriptionDetails,
                amount,
                nickname,
                name,
                interval,
                quantity: subscriptionDetails.quantity,
                endsAt: state.userSubscription.cancel_at,
            };
        }

        return {};
    },

    /**
     * Returns current user subscription plan (base item).
     *
     * @param   {object} state Vuex state.
     * @returns {object|null}       Subscription plan.
     */
    userSubscriptionPlan(state) {
        if (! state.billingPlans || ! state.userSubscription) {
            return null;
        }

        const priceIds = state.billingPlans?.flatMap(
            ({ prices }) => prices
                ? Object.values(prices).map(({ id }) => id)
                : []
        );

        return state.userSubscription?.items.data.find(
            ({ plan, price }) => !! priceIds.find(
                (item) => [plan.id, price.id].includes(item)
            )
        );
    },

    /**
     * Returns user upcoming invoice
     *
     * @param {object} state Vuex state.
     *
     * @returns  {object}         Upcoming invoice.
     */
    upcomingInvoice(state) {
        return state.upcomingInvoice || false;
    },

    /**
     * Returns true if there are any analytics data available for any show.
     *
     * @param  {object}  state Vuex state.
     * @returns {boolean}      Whether there are any analytics data.
     */
    hasAnalyticsData(state) {
        return !! sumBy(Object.values(state.shows), 'totalListens');
    },

    /**
     * Gets the list of show categories.
     *
     * @param   {object}   state Vuex state.
     * @returns {object[]}       Categories list.
     */
    showCategoriesList(state) {
        const categories = [];

        for (const value in state.showCategories) {
            const label = state.showCategories[value];

            categories.push({
                value: toString(value),
                label: label,
            });
        }

        return categories;
    },
};
